import { useEffect, useMemo, useState } from 'react';
import { Modal, COMMON_COUNTRIES } from './Modals.jsx';
import * as db from '../lib/data.js';
import { toCountryCode, originHeaderLevel, isDestinationHeader } from '../lib/origin.js';

// อ่าน "ประเทศผลิต" จาก packing list แล้วเติมให้ใบข้อมูลสินค้าในใบนี้ — 29 ต.ค. 2569
//   user: "การ์ดใหม่ควรใช้แหล่งกำเนิดจากข้อมูลจริง" (เดิมใบใหม่ได้ประเทศของ supplier ไปทั้งตู้)
//   ประเทศผลิตตัดสินสิทธิ์ FTA — ส่งจากญี่ปุ่นไม่ได้แปลว่าผลิตที่ญี่ปุ่น (ตู้ SP-261015)
//
// ⚠ กติกา:
//   ① ไม่เขียนอะไรจนกว่าจะกดยืนยัน
//   ② ใบที่ยังไม่มีประเทศ = เติมให้ (ติ๊กไว้) · ใบที่มีแล้วแต่ไม่ตรงกับไฟล์ = **ไม่ติ๊กให้** ต้องมีคนดูก่อน
//      (ใบข้อมูลสินค้าใช้ร่วมทุกตู้ · ของรุ่นเดียวกันบางล็อตผลิตคนละประเทศได้)
//   ③ ไฟล์บอกรหัสเดียวกันเป็นสองประเทศ = ไม่เติม บอกให้ดูเอง
//   ④ เลือกคอลัมน์จาก "ข้อมูลในคอลัมน์" ไม่ใช่ชื่อหัว — คอลัมน์ที่มีรหัสตรงกับของในใบนี้มากสุด = รหัส ·
//      คอลัมน์ที่อ่านเป็นชื่อประเทศได้มากสุด = ประเทศ (หัวตาราง PDF มักแตกหลายบรรทัดจนอ่านชื่อหัวไม่ได้)

const ccName = (c) => COMMON_COUNTRIES.find(([k]) => k === c)?.[1] ?? c;
const ccText = (c) => (c ? `${ccName(c)} (${c})` : 'ยังไม่ระบุ');

// รหัสของในใบนี้ → ใบข้อมูลสินค้า (ทั้งรหัสตาม invoice และรหัสของใบ — เขียนต่างกันได้)
function cardIndex(items) {
  const m = new Map();
  for (const li of items) {
    const c = li.product_classification;
    if (!c) continue;
    for (const k of new Set([db.normSku(li.sku), db.normSku(c.sku)])) if (k && !m.has(k)) m.set(k, c);
  }
  return m;
}

// รหัสของทุกบรรทัดในใบนี้ (มีใบข้อมูลสินค้าหรือยังไม่มีก็ได้) — ใช้หาคอลัมน์รหัส
//   🐛 รีวิว 29 ต.ค.: เดิมนับเฉพาะบรรทัดที่มีใบแล้ว → ตู้ที่เพิ่งนำเข้ายังไม่มีใบเลย = "ไม่เจอรหัสของใบนี้ในไฟล์" ทั้งที่ไฟล์ถูก
function lineKeys(items) {
  const keys = new Set();
  for (const li of items) {
    for (const k of [db.normSku(li.sku), db.normSku(li.product_classification?.sku)]) if (k) keys.add(k);
  }
  return keys;
}

// เลือกคอลัมน์จาก "ข้อมูลในคอลัมน์"
//   รหัส = คอลัมน์ที่มีรหัสตรงกับบรรทัดในใบนี้มากสุด
//   ประเทศ = นับเฉพาะ "ชื่อประเทศ" (CHINA · 中国製 · เวียดนาม) — รหัส 2 ตัวนับเฉพาะคอลัมน์ที่หัวบอกว่าเป็นประเทศ
//     (สี KH/PK/BE · หน่วย PK · Y/N "NO" ชนกับรหัสประเทศ — รีวิว 29 ต.ค. เจอคอลัมน์สีถูกเลือกแทน 原産国)
//   ต้องอ่านเป็นประเทศได้อย่างน้อยครึ่งหนึ่งของแถวที่รหัสตรง ไม่งั้นให้คนเลือกเอง (ไม่ข้ามไปหน้าตรวจ)
function guessCols(parsed, keys) {
  const n = parsed.headers.length;
  const codeHits = Array(n).fill(0);
  for (const r of parsed.rows) for (let i = 0; i < n; i++) if (keys.has(db.normSku(r[i]))) codeHits[i]++;
  let code = -1;
  for (let i = 0; i < n; i++) if (codeHits[i] && (code < 0 || codeHits[i] > codeHits[code])) code = i;
  if (code < 0) return { code: '', origin: '' };

  const matched = parsed.rows.filter(r => keys.has(db.normSku(r[code])));
  // คอลัมน์ที่ "พอ" (อ่านเป็นประเทศได้ ≥ ครึ่งของแถวที่รหัสตรง) แล้วจัดลำดับ: หัวชัดกว่าก่อน แล้วอ่านได้มากกว่า
  const cand = [];
  for (let i = 0; i < n; i++) {
    if (i === code) continue;
    const level = originHeaderLevel(parsed.headers[i]);
    if (isDestinationHeader(parsed.headers[i])) continue;   // ประเทศปลายทาง/ผู้รับ ห้ามเลือก (กติกาเดียวกับ originHeaderLevel)
    const hits = matched.filter(r => toCountryCode(r[i], { allowCode: level > 0 })).length;
    if (hits && hits * 2 >= matched.length) cand.push({ i, level, hits });
  }
  cand.sort((a, b) => b.level - a.level || b.hits - a.hits);
  // สองคอลัมน์สูสีกัน (หัวระดับเดียวกัน อ่านได้เท่ากัน) = ไม่เดา ให้คนเลือก
  const tie = cand.length > 1 && cand[0].level === cand[1].level && cand[0].hits === cand[1].hits;
  return { code: String(code), origin: cand.length && !tie ? String(cand[0].i) : '' };
}

function plan(parsed, cols, items) {
  const idx = cardIndex(items);
  // บรรทัดในใบนี้ที่ยังไม่มีใบข้อมูลสินค้า — แถวในไฟล์ที่ตรงกับพวกนี้ไม่ใช่ "ของใบอื่น" แต่ต้องสร้างใบก่อน
  const bare = new Set(items.filter(li => !li.product_classification).map(li => db.normSku(li.sku)).filter(Boolean));
  const ci = Number(cols.code), oi = Number(cols.origin);
  const per = new Map();     // card.id → { card, codes:Set, raws:Set }
  let outside = 0, needCard = 0;
  const unreadable = new Set();
  for (const r of parsed.rows) {
    const k = db.normSku(r[ci]);
    if (!k) continue;
    const card = idx.get(k);
    if (!card) { if (bare.has(k)) needCard++; else outside++; continue; }
    const raw = String(r[oi] ?? '').trim();
    const cc = toCountryCode(raw);
    const e = per.get(card.id) ?? { card, codes: new Set(), raws: new Set() };
    if (cc) e.codes.add(cc); else if (raw) { e.raws.add(raw); unreadable.add(raw); }
    per.set(card.id, e);
  }
  const fill = [], diff = [], same = [], conflict = [], noValue = [], unreadCards = [];
  // ตัวอย่างค่าที่อ่านได้ ให้คนเห็นว่าอ่านถูกคอลัมน์ ("CHINA" → จีน)
  const samples = new Map();
  for (const r of parsed.rows) {
    if (samples.size >= 4) break;
    const raw = String(r[oi] ?? '').trim(), cc = toCountryCode(raw);
    if (raw && cc && !samples.has(raw)) samples.set(raw, cc);
  }
  for (const e of per.values()) {
    const from = String(e.card.origin_country ?? '').trim().toUpperCase() || null;
    if (e.codes.size > 1) { conflict.push({ ...e, from }); continue; }
    if (!e.codes.size) { (e.raws.size ? unreadCards : noValue).push({ ...e, from }); continue; }
    const to = [...e.codes][0];
    const row = { id: e.card.id, card: e.card, from, to };
    if (!from) fill.push(row); else if (from === to) same.push(row); else diff.push(row);
  }
  const seen = new Set(per.keys());
  const cardsHere = [...new Map(items.filter(li => li.product_classification)
    .map(li => [li.product_classification.id, li.product_classification])).values()];
  const absent = cardsHere.filter(c => !seen.has(c.id));
  const noCard = items.filter(li => !li.product_classification).length;
  return { fill, diff, same, conflict, noValue, unreadCards, absent, noCard, needCard, outside, unreadable: [...unreadable],
    samples: [...samples] };
}

const Name = ({ c }) => <span className="of-nm">{c.name_en || c.name_th || '—'}</span>;

export default function OriginFromFile({ file, items, onClose, onDone }) {
  const [stage, setStage] = useState('reading');   // reading | map | review | saving | error
  const [err, setErr] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [cols, setCols] = useState({ code: '', origin: '' });
  const [take, setTake] = useState({});            // ใบที่ "ไม่ตรงกับไฟล์" ที่คนติ๊กให้เปลี่ยนตามไฟล์ { id: true }

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const pi = await import('../lib/pi-parse.js');   // lazy — xlsx + pdf.js หนัก
        // CSV อ่านเป็นข้อความ UTF-8 เอง — pi.parseFile ส่งไบต์ให้ XLSX ซึ่งเดาเป็น Latin-1 เมื่อไม่มี BOM
        //   (ไฟล์จาก Google Sheets / Numbers) → "中国" "จีน" กลายเป็นตัวอักษรเพี้ยน อ่านประเทศไม่ได้สักแถว (รีวิว 29 ต.ค.)
        const p = /\.csv$/i.test(file.name) || file.type === 'text/csv'
          ? await (async () => {
              const XLSX = await import('xlsx');
              const wb = XLSX.read((await file.text()).replace(/^\uFEFF/, ''), { type: 'string' });
              const grid = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: false, defval: '' })
                .map(r => r.map(c => String(c ?? '').trim()));
              return pi.finalizeGrid(grid);
            })()
          : await pi.parseFile(file);
        if (dead) return;
        const g = guessCols(p, lineKeys(items));
        setParsed(p); setCols(g);
        setStage(g.code && g.origin ? 'review' : 'map');
      } catch (e) {
        if (!dead) { setErr(e.message ?? String(e)); setStage('error'); }
      }
    })();
    return () => { dead = true; };
  }, [file]);

  const res = useMemo(() => (parsed && cols.code !== '' && cols.origin !== '' && cols.code !== cols.origin
    ? plan(parsed, cols, items) : null), [parsed, cols, items]);

  const close = () => { if (stage !== 'saving') onClose(); };   // กำลังเขียน = ห้ามปิด (ผลจะหาย ไม่รู้ว่าเขียนไปกี่ใบ)

  const save = async () => {
    const changes = [...res.fill, ...res.diff.filter(r => take[r.id])]
      .map(r => ({ id: r.id, from: r.from, to: r.to }));
    if (!changes.length) return;
    setStage('saving'); setErr(null);
    // setCardOrigins ไม่โยนกลางทาง — คืนว่าบันทึก/ข้าม/พลาดกี่ใบ แล้วโหลดใบนี้ใหม่เสมอ
    //   (เดิมพลาดใบที่ 40 = 39 ใบแรกบันทึกแล้วแต่จอไม่รู้ กดซ้ำแล้วรายงานว่า "มีคนแก้ระหว่างนั้น" ผิด ๆ — รีวิว 29 ต.ค.)
    const out = await db.setCardOrigins(changes);
    const changed = out.written.filter(c => c.from).length;   // นับจากที่เขียนได้จริง ไม่ใช่ที่ติ๊กไว้
    onDone?.(`ประเทศผลิตจาก ${file.name}: บันทึก ${out.done} ใบ`
      + (changed ? ` (ในนี้เปลี่ยนจากค่าเดิม ${changed} ใบ)` : '')
      + (out.skipped.length ? ` · ข้าม ${out.skipped.length} ใบที่มีคนแก้ระหว่างนั้น — เปิดดูอีกครั้งได้` : '')
      + (out.failed.length ? ` · บันทึกไม่สำเร็จ ${out.failed.length} ใบ (${out.error ?? 'เชื่อมต่อไม่ได้'}) — กดอ่านไฟล์นี้อีกครั้งได้ ใบที่บันทึกแล้วจะขึ้นว่าตรงอยู่แล้ว` : '')
      + (res.conflict.length ? ` · ไฟล์บอกสองประเทศ ${res.conflict.length} ใบ ยังไม่ได้เติม` : ''),
      out.failed.length > 0);   // มีใบที่พลาด = แถบแดง
    onClose();
  };

  if (stage === 'reading') return (
    <Modal title="กำลังอ่าน packing list…" sub={file.name} onClose={close}>
      <p className="ii-wait">ไฟล์ PDF หลายหน้าอาจใช้เวลาสักครู่ — ยังไม่มีอะไรถูกบันทึก</p>
    </Modal>
  );
  if (stage === 'error') return (
    <Modal title="อ่านไฟล์นี้ไม่ได้" sub={file.name} onClose={close}>
      <div className="ferr">{err}</div>
      <div className="ii-help">
        อ่านได้: PDF ที่พิมพ์ออกจากระบบ · Excel (.xlsx .xls) · CSV — PDF ที่เป็นรูปสแกนอ่านไม่ได้
        ขอให้ช่วยขอไฟล์ Excel จากซัพพลายเออร์ หรือเติมประเทศผลิตทีละใบในหน้าสินค้า &amp; HS
      </div>
      <div className="mfoot"><button className="btn2" onClick={close}>ปิด</button></div>
    </Modal>
  );

  const colSelect = (key, label) => (
    <label className="ii-mrow">
      <span className="ii-mh">{label}</span>
      <select value={cols[key]} onChange={e => { setTake({}); setCols(c => ({ ...c, [key]: e.target.value })); }}>
        <option value="">— เลือกคอลัมน์ —</option>
        {parsed.headers.map((h, i) => <option key={i} value={String(i)}>{h || `คอลัมน์ ${i + 1}`}</option>)}
      </select>
    </label>
  );
  const mapper = (
    <div className="ii-map">
      {colSelect('code', 'คอลัมน์ไหนคือรหัสสินค้า?')}
      {colSelect('origin', 'คอลัมน์ไหนคือประเทศผลิต (Country of Origin)?')}
    </div>
  );

  if (stage === 'map' || !res) return (
    <Modal title="ฝากช่วยบอกว่าคอลัมน์ไหนคืออะไร" wide sub={file.name} onClose={close}>
      <div className="ii-note">
        {!cols.code
          ? 'ไม่เจอรหัสสินค้าของใบนี้ในไฟล์เลย — ไฟล์นี้อาจเป็นของอีกใบ หรือรหัสเขียนคนละแบบ'
          : 'ยังไม่แน่ใจว่าคอลัมน์ไหนคือประเทศผลิต — ฝากช่วยเลือกให้ · ถ้าไฟล์ไม่มีคอลัมน์ประเทศผลิต ดูจากฉลากสินค้าแทน'}
      </div>
      {mapper}
      <div className="ii-preview">
        <div className="ii-ct">3 แถวแรกในไฟล์</div>
        <table><tbody>
          {parsed.rows.slice(0, 3).map((r, i) => (
            <tr key={i}>{r.slice(0, 10).map((c, k) => <td key={k}>{String(c).slice(0, 22)}</td>)}</tr>
          ))}
        </tbody></table>
      </div>
      <div className="mfoot">
        <button className="btn2" onClick={close}>ยกเลิก</button>
        <button className="btn1" disabled={cols.code === '' || cols.origin === '' || cols.code === cols.origin}
          onClick={() => setStage('review')}>ดูผลที่อ่านได้</button>
      </div>
    </Modal>
  );

  const n = res.fill.length + res.diff.filter(r => take[r.id]).length;
  return (
    <Modal title="ตรวจประเทศผลิตก่อนบันทึก" wide
      sub={`${file.name} · ยังไม่บันทึกจนกว่าจะกดยืนยัน`} onClose={close}>
      {err && <div className="ferr">{err}</div>}

      <div className="ii-sum">
        <span className="ii-chip ok">เติมให้ใบที่ยังว่าง {res.fill.length}</span>
        <span className="ii-chip fix">ไม่ตรงกับที่ใบบอก {res.diff.length}</span>
        <span className="ii-chip">ตรงอยู่แล้ว {res.same.length}</span>
        {res.conflict.length > 0 && <span className="ii-chip new">ไฟล์บอกสองประเทศ {res.conflict.length}</span>}
      </div>

      <details className="ii-fold">
        <summary>อ่านจากคอลัมน์ “{parsed.headers[cols.code] || `คอลัมน์ ${+cols.code + 1}`}” กับ
          “{parsed.headers[cols.origin] || `คอลัมน์ ${+cols.origin + 1}`}”
          {res.samples.length > 0 && <> · อ่านได้เช่น {res.samples.map(([raw, cc]) => `${raw} = ${ccName(cc)}`).join(' · ')}</>}
          {' '}— ไม่ถูก กดเพื่อเปลี่ยน</summary>
        {mapper}
      </details>

      {res.fill.length > 0 && (
        <><div className="ii-h">เติมประเทศผลิตให้ใบที่ยังว่าง ({res.fill.length})</div>
          <div className="of-list">{res.fill.map(r => (
            <div className="of-row" key={r.id}>
              <span className="ii-sku">{r.card.sku}</span><Name c={r.card}/>
              <span className="of-to">{ccText(r.to)}</span>
            </div>))}
          </div></>
      )}

      {res.diff.length > 0 && (
        <><div className="ii-h fix">ไฟล์บอกไม่ตรงกับใบข้อมูลสินค้า ({res.diff.length}) — ไม่ได้ติ๊กให้ ฝากช่วยดูก่อน</div>
          <div className="ii-note">
            ใบข้อมูลสินค้าใช้ร่วมทุกตู้ — ติ๊กแล้วตู้อื่นที่มีรหัสนี้จะเห็นประเทศใหม่ด้วย ·
            ของรุ่นเดียวกันบางล็อตผลิตคนละประเทศได้ ถ้าไม่แน่ใจดูฉลากที่ตัวสินค้า
          </div>
          <div className="of-list">{res.diff.map(r => (
            <label className={`of-row pick ${take[r.id] ? 'on' : ''}`} key={r.id}>
              <input type="checkbox" checked={!!take[r.id]}
                onChange={e => setTake(t => ({ ...t, [r.id]: e.target.checked }))}/>
              <span className="ii-sku">{r.card.sku}</span><Name c={r.card}/>
              <span className="of-to">ใบบอก {ccText(r.from)} → ไฟล์บอก <b>{ccText(r.to)}</b></span>
            </label>))}
          </div></>
      )}

      {res.conflict.length > 0 && (
        <div className="ii-warn">
          ไฟล์บอกรหัสเดียวกันเป็นหลายประเทศ — ไม่ได้เติมให้ ขอให้ช่วยดูที่ฉลากหรือถามซัพ:{' '}
          {res.conflict.map(e => `${e.card.sku} (${[...e.codes].join('/')})`).join(' · ')}
        </div>
      )}
      {res.noValue.length > 0 && (
        <div className="ii-warn">
          เจอรหัสในไฟล์แต่ช่องประเทศว่าง {res.noValue.length} ใบ — ไม่ได้เติม
          (Excel ที่รวมเซลล์ประเทศหลายแถวเข้าด้วยกัน ค่าจะอยู่แถวบนแถวเดียว):{' '}
          {res.noValue.slice(0, 8).map(e => e.card.sku).join(' · ')}{res.noValue.length > 8 ? ' …' : ''}
        </div>
      )}
      {res.unreadable.length > 0 && (
        <div className="ii-fees">
          อ่านเป็นชื่อประเทศไม่ได้ {res.unreadable.length} ค่า ({res.unreadable.slice(0, 4).join(' · ')}
          {res.unreadable.length > 4 ? ' …' : ''}) — แถวพวกนี้ไม่ได้เติม
          {res.unreadCards.length > 0 && <> · ใบที่ไม่ได้เติมเพราะเรื่องนี้ {res.unreadCards.length} ใบ:{' '}
            {res.unreadCards.slice(0, 8).map(e => e.card.sku).join(' · ')}{res.unreadCards.length > 8 ? ' …' : ''}</>}
        </div>
      )}
      {(res.absent.length > 0 || res.noCard > 0 || res.outside > 0) && (
        <div className="ii-fees">
          {res.absent.length > 0 && <>ใบในตู้นี้ที่ไม่มีในไฟล์ {res.absent.length} ใบ · </>}
          {res.noCard > 0 && <>บรรทัดที่ยังไม่มีใบข้อมูลสินค้า {res.noCard} บรรทัด{res.needCard > 0 ? ` (ไฟล์มีประเทศให้ ${res.needCard} แถว)` : ''} — สร้างใบก่อนแล้วกดอ่านไฟล์นี้อีกครั้ง · </>}
          {res.outside > 0 && <>แถวในไฟล์ที่ไม่ใช่ของในใบนี้ {res.outside} แถว (ข้าม)</>}
        </div>
      )}

      <div className="mfoot">
        <button className="btn2" disabled={stage === 'saving'} onClick={close}>ยกเลิก</button>
        <button className="btn1" disabled={stage === 'saving' || !n} onClick={save}>
          {stage === 'saving' ? 'กำลังบันทึก…' : n ? `บันทึกประเทศผลิต ${n} ใบ` : 'ไม่มีอะไรต้องบันทึก'}
        </button>
      </div>
    </Modal>
  );
}
